import React, { useMemo, useState, useEffect } from "react";
import {  Typography, Grid, Tooltip, IconButton } from "@mui/material";
import Helmet from "react-helmet";
import { pageStore, PagedTable } from "../../../common";
import { FormControlLabel, Switch } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
    createStringDataField,
    createTimestampField,
    createStatusField,
    createRawHtmlField,
} from "../../../util/format";

import {
    StringEquators,
    dateTimeEquators,
    BooleanEquators
} from "../../../store/constants/pages";
import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../../partials/content/Portlet";

import { resetPage, set } from "../../../store/actions/pages";
import { RootState } from "../../../store/reducers";
import { allNotificationsLanding } from "../../../store/actions/pages";
import { useData } from "@pure-ptr/react";
import {

    Visibility,
    VisibilityOff,
} from "@mui/icons-material";
import {
    markRead,
    markUnread,
    markAllRead,
    markAllUnread,
    getUnreadCount
} from "../../../store/api/notificationsSubscriptions";
import {selectClientListTiny } from "../../../store/selectors/clients";
import { GridLevelActions } from "./AlllNotificationsGridLevelActions";
import { setUnreadCount } from "../../../store/actions/notifications";
import { selectIsSuperAdmin,selectIsPartner,selectIsPartnerUser } from "../../../store/selectors/session";
import { createDeepLink,getHyperLinkTitle,hypeLinksEnums,parseJson } from "../../../common/NotificiationsBar/NotificationsBar";

const { usePageStore, useGridFilters } = pageStore(
    "all-notifications-landing",
    {}
);
const DEFAULT_HIDDEN_FIELDS = [];
const DEFAULT_SORT = [];

const statusDropdownOptions = [
    {
        label: "Read",
        value: true,
    },
    {
        label: "Unread",
        value: false,
    },
];

export const AllNotificationsViewPage = () => {
    const [refresh, setRefresh] = useState(Promise.resolve());

    const dispatch = useDispatch();
    const currentClientId = useSelector(
        (state: RootState) => state?.session?.user?.preferredDashboardClientId
    );

    const isSuperAdmin = useSelector(selectIsSuperAdmin)
     const isPartner= useSelector(selectIsPartner)
    const isPartnerUser= useSelector(selectIsPartnerUser)

    const showAllAlerts = usePageStore((s) => {
        return s.showAllAlerts;
    });

    const pending = usePageStore((s) => s.pending);
    const tableState = useGridFilters();
    // Check if an advanced filter for clientId is already applied
    const hasAdvancedClientIdFilter = useMemo(() => {
        return tableState?.filters?.operands?.some((operand) =>
            operand?.filters?.some((f) => f.id === "notification.clientId")
        );
    }, [tableState?.filters]);

    function handleRefresh() {
        dispatch(set("all-notifications-landing", "list", []));
        setRefresh(Promise.resolve());
    }

    useEffect(() => {
        handleRefresh();
    }, [showAllAlerts]);

    useEffect(() => {
        const pageName = { name: "all-notifications-landing" };
        dispatch(set(pageName.name, "waitForCriteriaFilter", false));
        return () => {
            dispatch(resetPage(pageName.name));
        };
    }, [dispatch]);

    const notifications = usePageStore((s: any) => {
        return s;
    });
    const clientUsersCanAccess = useSelector(selectClientListTiny);

    const notificationsData = notifications?.list || [];
    const totalRecords = notifications?.page?.totalRecords || 0;

    const [actionPayload, setActionPayload] = useState<any>({});

    const { isPending: isActionPending } = useData(async () => {
        if (Object.keys(actionPayload).length === 0) return;

        if (actionPayload.type === "read") {
            await markRead(actionPayload.ids);
        } else if (actionPayload.type === "unread") {
            await markUnread(actionPayload.ids);
        } else if (actionPayload.type === "allRead") {
            await markAllRead(
                showAllAlerts
                    ? clientUsersCanAccess?.map(
                          (item) => item?.clientId || item?.id
                      )
                    : [currentClientId]
            );
        } else if (actionPayload.type === "allUnread") {
            await markAllUnread(
                showAllAlerts
                    ? clientUsersCanAccess?.map(
                          (item) => item?.clientId || item?.id
                      )
                    : [currentClientId]
            );
        }
        handleRefresh();
        const unreadCount = await getUnreadCount([currentClientId]);
        dispatch(setUnreadCount(unreadCount));
        setActionPayload({});
    }, [actionPayload]);

    return (
        <>
            <Helmet title="All Notifications" />
            <Portlet>
                <PortletHeader
                    title={"All Notifications"}
                    name="all-notifications-landing"
                    showSearchFilters={true}
                    showSearchFiltersDropdown={false}
                    handleRefresh={handleRefresh}
                    toolbar={(isSuperAdmin || isPartner || isPartnerUser) ?
                        <FormControlLabel
                            sx={{
                                "&.MuiFormControlLabel-root": {
                                    justifyContent: "right",
                                    width: "100%",
                                    position: "relative",
                                    top: "12px",
                                },
                            }}
                            control={
                                <Switch
                                    onChange={(e) => {
                                        dispatch(
                                            set(
                                                "all-notifications-landing",
                                                "showAllAlerts",
                                                e.target.checked
                                            )
                                        );
                                    }}
                                    checked={showAllAlerts}
                                    disabled={hasAdvancedClientIdFilter}
                                />
                            }
                            label="Show All Notifications"
                        />:<></>
                    }
                    includeAdvanceFilters={true}
                />
                <PortletBody>
                    <GridLevelActions
                        loading={isActionPending}
                        handleAction={(type, ids) =>
                            setActionPayload({ type, ids })
                        }
                        handleRefresh={handleRefresh}
                    />
                    <AllNotificationsDataGrid
                        loading={pending || isActionPending}
                        data={notificationsData}
                        totalRecords={totalRecords}
                        refresh={refresh}
                        dispatch={dispatch}
                        showAllAlerts={showAllAlerts}
                        currentClientId={currentClientId}
                        clientUsersCanAccess={clientUsersCanAccess}
                        handleAction={(type, ids) =>
                            setActionPayload({ type, ids })
                        }
                    />
                </PortletBody>
            </Portlet>
        </>
    );
};

const AllNotificationsDataGrid = ({
    refresh,
    data,
    loading,
    dispatch,
    showAllAlerts,
    currentClientId,
    totalRecords,
    handleAction,
    clientUsersCanAccess,
}) => {
    const fields = useMemo(
        () => [
                createStringDataField("notification.title", "Title", {
                sortable: true,
                equators: StringEquators,
                hideFromFilter:true,
                secondLevel: "title",
                onHover: true,
                anchorOrigin: {
                    vertical: 'center',
                    horizontal: 'center',
                },
                transformOrigin: {
                    vertical: 'center',
                    horizontal: 'left',
                },
                }),
                
                createStringDataField("notification.clientName", "Client Name", {
                sortable: false,
                equators: StringEquators,
                hideFromFilter:true,
                secondLevel: "clientName"
                }),

                createRawHtmlField("notification.body", "Message", {
                sortable: true,
                equators: StringEquators,
                width: 500,
                secondLevel: "body",
            }),
              createTimestampField("notification.createdAt", "Timestamp", {
                sortable: true,
                fieldType: "Date",
                secondLevel: "createdAt",
                equators: dateTimeEquators,
            }),
            
            createStringDataField("notification.category", "Event Type", {
                sortable: true,
                equators: StringEquators,
                secondLevel: "category",
            }),

                createStringDataField("notification.clientId", "Client Name", {
                equators: BooleanEquators,
                // sortable: true,
                hideFromGrid: true,
                isStrictDropdown:true,
                returnWholeRow: true,
                dropdownValues:
                    clientUsersCanAccess?.map(
                        (item) => ({value:item?.clientId,label:item?.name})
                    ) || [],
                width: "120px",
            }),

            
            createStatusField("isRead", "Status", {
                equators: BooleanEquators,
                returnWholeRow: true,
                isStrictDropdown: true,
                dropdownValues: statusDropdownOptions,
                altValues: { true: "Read", false: "Unread" },
                sortable: true,
            }),
        ],
        []
    );

    const actionColumn = (rowData) => {
        return (
            <>
                <Grid item>
                    <IconButton
                        className="p-1"
                        aria-label="Edit"
                        onClick={() =>
                            handleAction(!rowData?.isRead ? "read" : "unread", [
                                rowData?.notificationId,
                            ])
                        }
                        size="small"
                    >
                        <Tooltip
                            title={
                                rowData?.isRead
                                    ? "Mark as Unread"
                                    : "Mark as Read"
                            }
                        >
                            {!rowData?.isRead ? (
                                <Visibility />
                            ) : (
                                <VisibilityOff />
                            )}
                        </Tooltip>
                    </IconButton>
                </Grid>
            </>
        );
    };

    const IsShowLink = (item) => {
     var json = parseJson(item);
     if(json?.ShowLink != undefined){
        return json?.ShowLink;
     }
     return true;
    }
    const IsShowExternalLink = (item) => {
        var json = parseJson(item);
        if (json?.IsExternalLink != undefined) {
            return json?.IsExternalLink;
        }
        return false;
    };
    const getNotificationText = (item) => {
    var json = parseJson(item);
     if(json?.MessageText != undefined){
        return json?.MessageText;
     }
     return '';
    }

    const hoverData = (rowData) => {
        const hoverColumns = new Map();
        if(IsShowLink(rowData)){
        const hyperlinkTitle =
            hypeLinksEnums[getHyperLinkTitle(rowData?.notification?.category)] ||
            getHyperLinkTitle(rowData?.notification?.category);

        const deepLink = createDeepLink(
            rowData?.notification, hyperlinkTitle
        );
        const eventsTypeColumn = [
           (rowData.notification?.clientId==currentClientId) && {
                type: "LINK",
                label: <span style={{textTransform:"capitalize"}}>Review {hyperlinkTitle}</span>,
                data: deepLink,
            },
        ];

        hoverColumns.set("notification.title", eventsTypeColumn);

        return hoverColumns;
        }
        if (IsShowExternalLink(rowData)) {
            const hyperlinkTitle =
                hypeLinksEnums[
                    getHyperLinkTitle(rowData?.notification?.category)
                ] || getHyperLinkTitle(rowData?.notification?.category);

            const deepLink = createDeepLink(
                rowData?.notification,
                hyperlinkTitle,
            );
            const eventsTypeColumn = [
                rowData.notification?.clientId == currentClientId && {
                    type: "TAB",
                    label: (
                        <span style={{ textTransform: "capitalize" }}>
                            Open {hyperlinkTitle}
                        </span>
                    ),
                    data: deepLink,
                },
            ];

            hoverColumns.set("notification.title", eventsTypeColumn);

            return hoverColumns;
        } else {
            const eventsTypeColumn = [
                rowData.notification?.clientId == currentClientId && {
                    data: getNotificationText(rowData),
                },
            ];

            hoverColumns.set("notification.title", eventsTypeColumn);

            return hoverColumns;
        }
    };

    const tableFilter = useGridFilters();
    return (
        <>
            <PagedTable
                name="all-notifications-landing"
                fields={fields}
                loading={loading}
                data={data || []}
                paginationFilters={tableFilter}
                onLoad={(sort, filter, pagination) => {
                    let finalFilter = filter;

                    const isAdvancedFilterPresent = filter?.operands?.some(
                        (operand) =>
                            operand?.filters?.some(
                                (f) => f.id === "notification.clientId"
                            )
                    );

                    if (!isAdvancedFilterPresent && !showAllAlerts) {
                        const clientFilterOperand = {
                            operator: "and",
                            filters: [
                                {
                                    id: "notification.clientId",
                                    equator: "equals",
                                    value: currentClientId,
                                },
                            ],
                        };

                        finalFilter = {
                            ...filter,
                            operands: [
                                ...(filter?.operands || []),
                                clientFilterOperand,
                            ],
                        };
                    }
                    dispatch(
                        allNotificationsLanding(pagination, sort, finalFilter)
                    );
                }}
                hoverData={hoverData}
                refresh={refresh}
                defaultSort={DEFAULT_SORT}
                criteriasSaveEnabled={true}
                hasCheckbox={true}
                showSelectAll={true}
                showSearchFilters={true}
                defaultHiddenFields={DEFAULT_HIDDEN_FIELDS}
                hideRefresh={true}
                shouldRenderLazy={true}
                renderActionColumn={actionColumn}
                checkBoxSelectedFieldName="notificationId"
                hasActions={true}
                tableLevelActions={() => (
                    <Typography
                        sx={{ position: "absolute", left: 0, bottom: 0 }}
                    >
                        Total Notifications: {totalRecords}
                    </Typography>
                )}
            />
        </>
    );
};
